/*
 * <locations-map> — the full-viewport locator app on the locations index.
 *
 * Three jobs:
 *
 * 1. Sizing. The app fills the viewport below the header so the page never scrolls
 *    (only the store list does). CSS can't know the header stack's height, so this
 *    measures the element's own document offset and publishes it as
 *    --locations-app-top for section-locations.css to subtract from 100dvh.
 *
 * 2. Pins. Every row with data-lat/data-lng gets a Leaflet marker (self-hosted
 *    assets/leaflet.js, OpenStreetMap tiles — no API key). Markers and rows stay in
 *    sync both ways: a pin click highlights its row and scrolls the list to it;
 *    clicking a row card (or pressing Enter/Space while focused) flies the map to it.
 *
 * 3. Filtering. <locations-filter> hides rows via [hidden]; a MutationObserver
 *    mirrors that onto the markers and re-fits the map to what's left.
 *
 * Progressive enhancement: without JS the app falls back to a CSS-estimated height
 * and a <noscript> single-store Google embed; every row still works as plain links.
 */

const PIN_SVG = `
  <svg viewBox="0 0 24 32" focusable="false" aria-hidden="true">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z"/>
    <circle cx="12" cy="12" r="4.5"/>
  </svg>
`;

class LocationsMap extends HTMLElement {
  connectedCallback() {
    this.updateHeight = this.updateHeight.bind(this);
    this.updateHeight();
    window.addEventListener('resize', this.updateHeight);
    window.addEventListener('load', this.updateHeight);

    this.addEventListener('click', (event) => {
      const row = event.target.closest('[data-locations-filter-item]');
      if (!row || !this.contains(row)) return;
      if (event.target.closest('a, button, input, select, textarea, summary, [role="button"], [role="link"]')) return;
      this.focusRow(row);
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const row = event.target.closest('[data-locations-filter-item]');
      if (!row || !this.contains(row)) return;
      if (event.target.closest('a, button, input, select, textarea, summary, [role="button"], [role="link"]')) return;
      event.preventDefault();
      this.focusRow(row);
    });

    this.initMap();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.updateHeight);
    window.removeEventListener('load', this.updateHeight);
    this.observer?.disconnect();
    this.map?.remove();
  }

  /* Distance from the top of the document to the app = header + announcement bar +
     anything else above it, regardless of what the merchant stacks up there. */
  updateHeight() {
    const top = Math.max(0, Math.round(this.getBoundingClientRect().top + window.scrollY));
    this.style.setProperty('--locations-app-top', `${top}px`);
    this.map?.invalidateSize();
  }

  initMap() {
    const canvas = this.querySelector('[data-locations-map-canvas]');
    if (!canvas || typeof L === 'undefined') return;

    this.map = L.map(canvas, { zoomControl: true });

    // CARTO's Voyager basemap — the closest free raster style to the Google Maps
    // look (cream land, white roads, muted water). {r} serves @2x tiles on retina.
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=cb1_2usf_1_6fd16dc3d6347ef77e1e9824', {
      subdomains: 'abcd',
      maxZoom: 20,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions" target="_blank" rel="noopener">CARTO</a>',
    }).addTo(this.map);

    const icon = L.divIcon({
      className: 'locations-pin',
      html: PIN_SVG,
      iconSize: [30, 40],
      iconAnchor: [15, 38],
      popupAnchor: [0, -36],
      ariaLabel: 'Location marker',
      title: 'Location Marker'

    });

    this.entries = [];

    

    for (const row of this.querySelectorAll('[data-locations-filter-item]')) {
      const lat = parseFloat(row.dataset.lat);
      const lng = parseFloat(row.dataset.lng);
      if (Number.isNaN(lat) || Number.isNaN(lng)) continue;

      //const marker = L.marker([lat, lng], { icon, alt: row.dataset.locationName ?? '' });

      const marker = L.marker([lat, lng], { icon, alt: 'Description of the location', title: 'Location Name'  ?? '' });
      marker.bindPopup(this.popupContent(row), { closeButton: true });
      marker.on('click', () => this.setCurrent(row, { scrollList: true }));
      marker.addTo(this.map);

      this.entries.push({ row, marker, onMap: true });
    }

    this.fitToVisible({ animate: false });

    // Mirror the search filter onto the pins: rows toggle [hidden], markers follow.
    this.observer = new MutationObserver(() => this.syncMarkers());
    for (const { row } of this.entries) {
      this.observer.observe(row, { attributes: true, attributeFilter: ['hidden'] });
    }
  }

  popupContent(row) {
    const name = row.querySelector('.location-row__link');
    const address = row.querySelector('.location-row__address');
    const orderNow = row.querySelector('.location-row__order-button');
    const directions = row.querySelector('a.location-row__action[href*="google.com/maps/dir"]');

    const content = document.createElement('div');
    content.className = 'locations-popup';

    if (name) {
      const link = document.createElement('a');
      link.className = 'locations-popup__name';
      link.href = name.href;
      link.textContent = name.textContent.trim();
      content.appendChild(link);
    }

    if (address) {
      const line = document.createElement('p');
      line.className = 'locations-popup__address';
      line.textContent = address.textContent.trim().replace(/\s+/g, ' ');
      content.appendChild(line);
    }

    const actions = document.createElement('div');
    actions.className = 'locations-popup__actions';

    if (orderNow) {
      const link = document.createElement('a');
      link.className = 'locations-popup__order';
      link.href = orderNow.href;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = 'Order now';
      actions.appendChild(link);
    }

    if (directions) {
      const link = document.createElement('a');
      link.className = 'locations-popup__directions';
      link.href = directions.href;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = 'Get directions';
      actions.appendChild(link);
    }

    if (actions.childElementCount > 0) {
      content.appendChild(actions);
    }

    return content;
  }

  entryFor(row) {
    return this.entries?.find((entry) => entry.row === row);
  }

  /* Card pressed on a row */
  focusRow(row) {
    const entry = this.entryFor(row);
    if (!entry) return;

    const target = entry.marker.getLatLng();
    const zoom = Math.max(this.map.getZoom(), 14);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.map.setView(target, zoom);
    } else {
      this.map.flyTo(target, zoom, { duration: 0.6 });
    }

    entry.marker.openPopup();
    this.setCurrent(row);
  }

  setCurrent(row, { scrollList = false } = {}) {
    for (const entry of this.entries) {
      const isCurrent = entry.row === row;
      entry.row.toggleAttribute('aria-current', isCurrent);
      entry.marker.getElement()?.classList.toggle('locations-pin--current', isCurrent);
      entry.marker.setZIndexOffset(isCurrent ? 1000 : 0);
    }

    if (scrollList) this.scrollRowToTop(row);
  }

  /* Picking a pin brings its card to the top of the list panel, like the
     results pane of a maps app. */
  scrollRowToTop(row) {
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const list = this.querySelector('[data-locations-list]');

    if (!list) {
      row.scrollIntoView({ behavior, block: 'start' });
      return;
    }

    // Leave room for a pinned region divider so it doesn't cover the card.
    const stickyHeader = row.closest('[data-locations-region]')?.querySelector('.locations__region-header');
    const headroom = (stickyHeader?.offsetHeight ?? 0) + 12;

    const offset = row.getBoundingClientRect().top - list.getBoundingClientRect().top;
    list.scrollTo({ top: list.scrollTop + offset - headroom, behavior });
  }

  syncMarkers() {
    let changed = false;

    for (const entry of this.entries) {
      const shouldShow = !entry.row.hidden;
      if (shouldShow === entry.onMap) continue;

      if (shouldShow) {
        entry.marker.addTo(this.map);
        // Re-added markers get fresh DOM elements, so restore the selected look.
        entry.marker.getElement()?.classList.toggle('locations-pin--current', entry.row.hasAttribute('aria-current'));
      } else {
        entry.marker.remove();
      }
      entry.onMap = shouldShow;
      changed = true;
    }

    if (changed) this.fitToVisible({ animate: true });
  }

  fitToVisible({ animate }) {
    const visible = this.entries.filter((entry) => entry.onMap);
    if (visible.length === 0) return;

    const bounds = L.latLngBounds(visible.map((entry) => entry.marker.getLatLng()));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.map.fitBounds(bounds, {
      padding: [48, 48],
      maxZoom: 14,
      animate: animate && !reduceMotion,
    });
  }
}

customElements.define('locations-map', LocationsMap);
